import { useCallback, useEffect, useState } from 'react';

import Editor from '@components/Editor';
import { Button } from '@components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select';
import { ETypeNote, typeNote } from '@constants/typeNote';
import errorsValidation from '@errors/validation';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ICreateNoteInputData } from '@services/notes/dtos/createNote/InputData';
import { type IUpdateNoteInputData } from '@services/notes/dtos/updateNote/InputData';
import NoteService from '@services/notes/NoteService';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LoaderCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

const formSchema = z.object({
  title: z.string().min(1, {
    message: errorsValidation.required,
  }),
  type: z.enum([ETypeNote.ConditionOrDisease, ETypeNote.Medicine]),
  content: z.string().min(1, {
    message: errorsValidation.required,
  }),
  presentation: z.string().optional(),
});

interface IProps {
  content?: string;
  presentation?: string | null;
  title?: string;
  type?: ETypeNote;
}

function NoteForm({ content, presentation, title, type }: IProps) {
  const queryClient = useQueryClient();
  const [titlePlaceholder, setTitlePlaceholder] =
    useState<string>('Nome da doença');
  const [contentPlaceholder, setContentPlaceholder] = useState<string>(
    'Anotações sobre a doença',
  );
  const [hasPresentation, setHasPresentation] = useState(false);
  const navigation = useNavigate();
  const params = useParams();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    defaultValues: {
      title: title || '',
      content: content || '',
      presentation: presentation || '',
      type: type || ETypeNote.ConditionOrDisease,
    },
  });

  const { isPending: isPendingCreate, mutateAsync: mutateAsyncCreate } =
    useMutation<void, unknown, ICreateNoteInputData>({
      mutationFn: NoteService.create,
      onError: () => {
        toast.error('Atenção', {
          description: 'Não foi possível criar a anotação. Tente novamente',
        });
      },
      onSuccess: () => {
        toast.success('Sucesso!', {
          description: 'Anotação criada com sucesso',
        });
        navigation(`/anotacoes${params.id ? `/${params.id}` : ''}`, {
          replace: true,
        });
      },
    });

  const { isPending: isPendingUpdate, mutateAsync: mutateAsyncUpdate } =
    useMutation<void, unknown, IUpdateNoteInputData>({
      mutationFn: NoteService.update,
      onError: () => {
        toast.error('Atenção', {
          description: 'Não foi possível editar a anotação. Tente novamente',
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['noteDetails'] });
        toast.success('Sucesso!', {
          description: 'Anotação editada com sucesso',
        });
        navigation(`/anotacoes${params.id ? `/${params.id}` : ''}`, {
          replace: true,
        });
      },
    });

  const handleSubmit = useCallback(
    async (values: z.infer<typeof formSchema>) => {
      if (params.fileId) {
        await mutateAsyncUpdate({
          content: values.content,
          title: values.title,
          type: values.type,
          presentation: values.presentation || null,
          id: params.fileId,
        });
      } else {
        await mutateAsyncCreate({
          content: values.content,
          folderId: params.id ? params.id : null,
          title: values.title,
          type: values.type,
          presentation: values.presentation || null,
        });
      }
    },
    [mutateAsyncCreate, mutateAsyncUpdate, params.fileId, params.id],
  );

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'type' && value.type) {
        if (value.type === ETypeNote.Medicine) {
          setHasPresentation(true);
          setTitlePlaceholder('Dipirona');
          setContentPlaceholder('Tomar 1 cp de 6/6 horas, se dor ou febre');
        } else {
          form.setValue('presentation', '');
          setHasPresentation(false);
          setTitlePlaceholder('Nome da doença');
          setContentPlaceholder('Anotações sobre a doença');
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  useEffect(() => {
    if (type === ETypeNote.Medicine) {
      setHasPresentation(true);
    }
  }, [type]);

  return (
    <Form {...form}>
      <form className="space-y-6" onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="grid grid-cols-3 gap-2">
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem className="flex flex-col self-stretch">
                <FormLabel className="text-tertiary">Tipo</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger onBlur={field.onBlur}>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>

                  <SelectContent>
                    {typeNote.inputPickerOptions.map((type) => (
                      <SelectItem
                        className="focus:bg-tertiary"
                        key={type.value}
                        value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem className="flex flex-col self-stretch">
                <FormLabel className="text-tertiary">Título</FormLabel>
                <FormControl>
                  <Input
                    className="border-tertiary"
                    placeholder={titlePlaceholder}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {hasPresentation && (
            <FormField
              control={form.control}
              name="presentation"
              render={({ field }) => (
                <FormItem className="flex flex-col self-stretch">
                  <FormLabel className="text-tertiary">Apresentação</FormLabel>
                  <FormControl>
                    <Input
                      className="border-tertiary"
                      placeholder="500mg ou 1g"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
        <FormField
          control={form.control}
          name="content"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel className="text-tertiary">Descrição</FormLabel>
              <FormControl>
                <Editor
                  content={field.value}
                  onChangeText={field.onChange}
                  placeholder={contentPlaceholder}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex flex-col-reverse justify-end gap-4 sm:flex-row">
          <Button
            className="min-w-36 cursor-pointer"
            disabled={isPendingCreate || isPendingUpdate}
            onClick={() => navigation(-1)}
            type="button"
            variant="destructive">
            Voltar
          </Button>
          <Button
            className="min-w-36 cursor-pointer"
            disabled={isPendingCreate || isPendingUpdate}
            variant="secondary">
            {isPendingCreate || isPendingUpdate ? (
              <LoaderCircle className="text-background animate-spin" />
            ) : (
              'Salvar'
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}

export default NoteForm;
